import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Divider } from '../../../components/ui/Divider';
import { FieldInput } from '../../../components/ui/FieldInput';
import { CustomCheckbox } from '../../../components/ui/CustomCheckbox';
import { TextArea } from '../../../components/ui/TextArea';
import {
  fetchCustomer, createCustomer, updateCustomer,
  fetchBankRequisites, createBankRequisite, updateBankRequisite, deleteBankRequisite, setDefaultBankRequisite,
  fetchContacts, createContact, updateContact, deleteContact
} from './api';
import { CustomerRead, CustomerBankRead, CustomerContactRead } from './types';
import { SaveOutlined, ArrowBackOutlined, AddOutlined, CheckCircle, Edit, Delete } from '@mui/icons-material';

const CustomerFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  // --- Состояния ---
  // Блок 1 (Заказчик)
  const [customerForm, setCustomerForm] = useState<Partial<CustomerRead>>({ full_name: '', short_name: '', legal_address: '', phone: '', email: '' });

  // Создание вместе с заказчиком
  const [addBankOnCreate, setAddBankOnCreate] = useState(false);
  const [addContactOnCreate, setAddContactOnCreate] = useState(false);

  // Блок 2 (Реквизиты)
  const [banks, setBanks] = useState<CustomerBankRead[]>([]);
  const [showBankForm, setShowBankForm] = useState(false);
  const [editingBankId, setEditingBankId] = useState<number | null>(null);
  const [bankForm, setBankForm] = useState<Partial<CustomerBankRead>>({ bank_name: '', bank_account: '', corr_account: '', bik: '', is_default: true });

  // Блок 3 (Контакты)
  const [contacts, setContacts] = useState<CustomerContactRead[]>([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContactId, setEditingContactId] = useState<number | null>(null);
  const [contactForm, setContactForm] = useState<Partial<CustomerContactRead>>({ full_name: '', short_name: '', position: '', basis_of_authority: '', phone: '', email: '', is_signatory: false, is_active: true });

  useEffect(() => {
    if (!isNew && id) {
      loadData(Number(id));
    }
  }, [id, isNew]);

  const loadData = async (orgId: number) => {
    try {
      const customer = await fetchCustomer(orgId);
      setCustomerForm(customer);
      const bks = await fetchBankRequisites(orgId, true);
      setBanks(bks);
      const cnts = await fetchContacts(orgId, true, false);
      setContacts(cnts);
    } catch (err) {
      console.error('Ошибка загрузки данных', err);
    }
  };

  // --- ОБРАБОТЧИКИ ЗАКАЗЧИКА ---
  const handleSaveCustomer = async () => {
    if (!customerForm.full_name || !customerForm.legal_address) {
      alert('Заполните обязательные поля заказчика (Полное наименование, Юридический адрес)');
      return;
    }
    if (isNew && addBankOnCreate && (!bankForm.bank_name || !bankForm.bik || !bankForm.bank_account)) {
      alert('Заполните обязательные поля банка (Название, БИК, Расчетный счет)');
      return;
    }
    if (isNew && addContactOnCreate && !contactForm.full_name) {
      alert('Заполните обязательные поля контакта (ФИО)');
      return;
    }

    try {
      if (isNew) {
        const res = await createCustomer(customerForm as any);

        if (addBankOnCreate) {
          await createBankRequisite(res.id, { ...bankForm, is_default: bankForm.is_default ?? true } as any);
        }

        if (addContactOnCreate) {
          await createContact(res.id, { ...contactForm, is_active: true } as any);
        }

        navigate(`/organizations/customers/${res.id}`, { replace: true });
      } else {
        await updateCustomer(Number(id), customerForm as any);
        alert('Данные заказчика успешно обновлены');
      }
    } catch (err) {
      alert('Ошибка при сохранении. Проверьте правильность заполнения полей.');
    }
  };

  // --- ОБРАБОТЧИКИ РЕКВИЗИТОВ ---
  const saveBank = async () => {
    try {
      if (editingBankId) {
        await updateBankRequisite(editingBankId, bankForm as any);
      } else {
        await createBankRequisite(Number(id), bankForm as any);
      }
      setShowBankForm(false);
      loadData(Number(id));
    } catch (err) { alert('Ошибка сохранения реквизитов'); }
  };

  const removeBank = async (reqId: number) => {
    if (window.confirm('Удалить реквизиты?')) {
      await deleteBankRequisite(reqId);
      loadData(Number(id));
    }
  };

  const setBankDefault = async (reqId: number) => {
    await setDefaultBankRequisite(reqId);
    loadData(Number(id));
  };

  // --- ОБРАБОТЧИКИ КОНТАКТОВ ---
  const saveContact = async () => {
    try {
      if (editingContactId) {
        await updateContact(editingContactId, contactForm as any);
      } else {
        await createContact(Number(id), contactForm as any);
      }
      setShowContactForm(false);
      loadData(Number(id));
    } catch (err) { alert('Ошибка сохранения контакта'); }
  };

  const removeContact = async (contactId: number) => {
    if (window.confirm('Удалить контакт?')) {
      await deleteContact(contactId);
      loadData(Number(id));
    }
  };

  const renderBankFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FieldInput label="Название банка" required value={bankForm.bank_name || ''} onChange={v => setBankForm({ ...bankForm, bank_name: v })} />
      <FieldInput label="БИК" required value={bankForm.bik || ''} onChange={v => setBankForm({ ...bankForm, bik: v })} />
      <FieldInput label="Расчетный счет" required value={bankForm.bank_account || ''} onChange={v => setBankForm({ ...bankForm, bank_account: v })} />
      <FieldInput label="Корр. счет" value={bankForm.corr_account || ''} onChange={v => setBankForm({ ...bankForm, corr_account: v })} />
      <div className="md:col-span-2 mt-2">
        <CustomCheckbox id="is_default_bank" checked={!!bankForm.is_default} onChange={() => setBankForm({ ...bankForm, is_default: !bankForm.is_default })} label="Использовать как счет по умолчанию" />
      </div>
    </div>
  );

  const renderContactFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <FieldInput label="ФИО" required value={contactForm.full_name || ''} onChange={v => setContactForm({ ...contactForm, full_name: v })} />
      <FieldInput label="Краткое имя" value={contactForm.short_name || ''} onChange={v => setContactForm({ ...contactForm, short_name: v })} />
      <FieldInput label="Должность" value={contactForm.position || ''} onChange={v => setContactForm({ ...contactForm, position: v })} />
      <div className="md:col-span-3">
        <FieldInput label="Основание полномочий (Действует на основании...)" value={contactForm.basis_of_authority || ''} onChange={v => setContactForm({ ...contactForm, basis_of_authority: v })} />
      </div>
      <FieldInput label="Телефон" value={contactForm.phone || ''} onChange={v => setContactForm({ ...contactForm, phone: v })} />
      <FieldInput label="Email" value={contactForm.email || ''} onChange={v => setContactForm({ ...contactForm, email: v })} />
      <div className="md:col-span-3 flex flex-col gap-3 mt-2">
        <CustomCheckbox id="is_signatory" checked={!!contactForm.is_signatory} onChange={() => setContactForm({ ...contactForm, is_signatory: !contactForm.is_signatory })} label="Является подписантом (для генерации документов)" />
        {!isNew && <CustomCheckbox id="is_active_contact" checked={!!contactForm.is_active} onChange={() => setContactForm({ ...contactForm, is_active: !contactForm.is_active })} label="Активный контакт" />}
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-6 pb-20">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/organizations/customers')} className="text-gray-500 hover:text-gray-800">
          <ArrowBackOutlined />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">{isNew ? 'Новый заказчик' : 'Редактирование заказчика'}</h1>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-5">Основная информация</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldInput label="Полное наименование" required value={customerForm.full_name || ''} onChange={v => setCustomerForm({ ...customerForm, full_name: v })} />
          <FieldInput label="Краткое наименование" value={customerForm.short_name || ''} onChange={v => setCustomerForm({ ...customerForm, short_name: v })} />
          <FieldInput label="Телефон" value={customerForm.phone || ''} onChange={v => setCustomerForm({ ...customerForm, phone: v })} />
          <FieldInput label="Email" value={customerForm.email || ''} onChange={v => setCustomerForm({ ...customerForm, email: v })} />
          <div className="md:col-span-2">
            <TextArea label="Юридический адрес" required value={customerForm.legal_address || ''} onChange={v => setCustomerForm({ ...customerForm, legal_address: v })} />
          </div>
        </div>

        {isNew && (
          <div className="mt-8 pt-6 border-t border-gray-100 space-y-6">
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <CustomCheckbox id="add_bank_chk" checked={addBankOnCreate} onChange={() => setAddBankOnCreate(!addBankOnCreate)} label="Сразу добавить банковские реквизиты" />
              {addBankOnCreate && <div className="mt-4 pt-4 border-t border-gray-200">{renderBankFields()}</div>}
            </div>

            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <CustomCheckbox id="add_contact_chk" checked={addContactOnCreate} onChange={() => setAddContactOnCreate(!addContactOnCreate)} label="Сразу добавить контактное лицо (Заказчика)" />
              {addContactOnCreate && <div className="mt-4 pt-4 border-t border-gray-200">{renderContactFields()}</div>}
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button onClick={handleSaveCustomer} className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-md hover:bg-blue-700 shadow-sm transition-colors">
            <SaveOutlined fontSize="small" /> Сохранить {isNew ? 'заказчика' : 'профиль'}
          </button>
        </div>
      </div>

      {!isNew && (
        <>
          <Divider />

          {/* БЛОК 2: Банковские реквизиты */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Банковские реквизиты</h2>
              <button
                onClick={() => { setBankForm({ bank_name: '', bank_account: '', corr_account: '', bik: '', is_default: banks.length === 0 }); setEditingBankId(null); setShowBankForm(true); }}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium"
              >
                <AddOutlined fontSize="small" /> Добавить счет
              </button>
            </div>

            {banks.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium uppercase text-xs">Банк / БИК</th>
                      <th className="px-4 py-3 text-left font-medium uppercase text-xs">Расч. счет / Корр. счет</th>
                      <th className="px-4 py-3 text-center font-medium uppercase text-xs w-32">Основной</th>
                      <th className="px-4 py-3 text-right font-medium uppercase text-xs w-24">Действия</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {banks.map(b => (
                      <tr key={b.id} className={!b.is_active ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-3"><b>{b.bank_name}</b><br/><span className="text-xs text-gray-500">БИК: {b.bik}</span></td>
                        <td className="px-4 py-3 font-mono">{b.bank_account}<br/><span className="text-xs text-gray-500">{b.corr_account}</span></td>
                        <td className="px-4 py-3 text-center">
                          {b.is_default ? <CheckCircle className="text-green-500" fontSize="small" /> : (
                            <button onClick={() => setBankDefault(b.id)} className="text-xs text-blue-500 hover:text-blue-700 underline underline-offset-2">Сделать основным</button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right space-x-3">
                          <button onClick={() => { setBankForm(b); setEditingBankId(b.id); setShowBankForm(true); }} className="text-gray-400 hover:text-blue-600"><Edit fontSize="small" /></button>
                          <button onClick={() => removeBank(b.id)} className="text-gray-400 hover:text-red-600"><Delete fontSize="small" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {showBankForm && (
              <div className="bg-blue-50/40 border border-blue-200 p-5 rounded-lg shadow-inner">
                <h3 className="text-base font-semibold text-blue-900 mb-4">{editingBankId ? 'Редактировать счет' : 'Новый счет'}</h3>
                {renderBankFields()}
                <div className="mt-5 flex gap-3">
                  <button onClick={saveBank} className="bg-blue-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-blue-700 shadow-sm">Сохранить</button>
                  <button onClick={() => setShowBankForm(false)} className="bg-white border border-gray-300 text-gray-700 px-5 py-2 rounded-md text-sm font-medium hover:bg-gray-50 shadow-sm">Отмена</button>
                </div>
              </div>
            )}
          </div>

          <Divider />

          {/* БЛОК 3: Контакты заказчика */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Контакты (Представители)</h2>
              <button
                onClick={() => { setContactForm({ full_name: '', position: '', basis_of_authority: '', phone: '', email: '', is_signatory: false, is_active: true }); setEditingContactId(null); setShowContactForm(true); }}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium"
              >
                <AddOutlined fontSize="small" /> Добавить контакт
              </button>
            </div>

            {contacts.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium uppercase text-xs">ФИО / Должность</th>
                      <th className="px-4 py-3 text-left font-medium uppercase text-xs">Контакты</th>
                      <th className="px-4 py-3 text-center font-medium uppercase text-xs w-32">Подписант</th>
                      <th className="px-4 py-3 text-right font-medium uppercase text-xs w-24">Действия</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {contacts.map(c => (
                      <tr key={c.id} className={!c.is_active ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-3"><b className="text-gray-900">{c.full_name}</b><br/><span className="text-xs text-gray-500">{c.position || '—'}</span></td>
                        <td className="px-4 py-3">{c.phone || '—'}<br/><span className="text-xs text-gray-500">{c.email || '—'}</span></td>
                        <td className="px-4 py-3 text-center">{c.is_signatory ? <CheckCircle className="text-green-500" fontSize="small" /> : '—'}</td>
                        <td className="px-4 py-3 text-right space-x-3">
                          <button onClick={() => { setContactForm(c); setEditingContactId(c.id); setShowContactForm(true); }} className="text-gray-400 hover:text-blue-600"><Edit fontSize="small" /></button>
                          <button onClick={() => removeContact(c.id)} className="text-gray-400 hover:text-red-600"><Delete fontSize="small" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {showContactForm && (
              <div className="bg-blue-50/40 border border-blue-200 p-5 rounded-lg shadow-inner">
                <h3 className="text-base font-semibold text-blue-900 mb-4">{editingContactId ? 'Редактировать контакт' : 'Новый контакт'}</h3>
                {renderContactFields()}
                <div className="mt-5 flex gap-3">
                  <button onClick={saveContact} className="bg-blue-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-blue-700 shadow-sm">Сохранить</button>
                  <button onClick={() => setShowContactForm(false)} className="bg-white border border-gray-300 text-gray-700 px-5 py-2 rounded-md text-sm font-medium hover:bg-gray-50 shadow-sm">Отмена</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerFormPage;